import {
  CONTEXT_PRIVATE_DATA_NOTE,
  type ContextProfileDto,
  saveContextProfileInput,
} from '@fos/shared/contracts/context';
import { CONTEXT_PROFILE_KIND_LABELS } from '@fos/shared/labels';
import { History, Pencil } from 'lucide-react';
import { type FormEvent, useState } from 'react';
import { appErrorCode, errorMessage, validationIssues } from '../../../lib/api';
import { formatDate } from '../../../lib/format';
import { trpc } from '../../../lib/trpc';
import { FormMessage } from '../../../ui/Blocks';
import { Button } from '../../../ui/Button';
import { Field } from '../../../ui/Field';
import { Input } from '../../../ui/Input';
import { Markdown } from '../../../ui/Markdown';
import { Mono } from '../../../ui/Mono';
import { toast } from '../../../ui/Toast';
import { NoteEditor } from '../../knowledge/components/NoteEditor';
import { TOUCH_BUTTON } from '../../mobile';
import { ProfileHistoryDialog } from './ProfileHistoryDialog';

/** Linia meta profilu: „OWNER.md · wersja 4 · 01.10.2026 · Sebastian”. */
export function profileMeta(profile: ContextProfileDto): string {
  if (profile.versionNo === 0) return 'Jeszcze pusty';
  return [
    `wersja ${profile.versionNo}`,
    profile.createdAt ? formatDate(profile.createdAt) : null,
    profile.author?.name ?? null,
  ]
    .filter(Boolean)
    .join(' · ');
}

/**
 * Profil kontekstu (OWNER.md albo BUSINESS.md): treść, wersja, autor; „Edytuj” (właściciel i admin) zapisuje nową
 * wersję z opisem zmiany; „Historia” pokazuje wersje z diffem. Zmiana w międzyczasie = CONFLICT („Wczytaj”).
 */
export function ProfileCard({ profile, onSaved }: { profile: ContextProfileDto; onSaved?: () => void }) {
  const utils = trpc.useUtils();
  const [editing, setEditing] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [body, setBody] = useState(profile.bodyMd);
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [bodyError, setBodyError] = useState<string | null>(null);
  const [conflict, setConflict] = useState(false);
  const label = CONTEXT_PROFILE_KIND_LABELS[profile.kind];
  const headingId = `profile-${profile.kind}`;

  const save = trpc.context.saveProfile.useMutation({
    onSuccess: (saved) => {
      toast.success(`${saved.file}: zapisano wersję ${saved.versionNo}`);
      void utils.context.overview.invalidate();
      void utils.context.listVersions.invalidate({ kind: profile.kind });
      setEditing(false);
      onSaved?.();
    },
    onError: (err) => {
      if (appErrorCode(err) === 'CONFLICT') {
        setConflict(true);
        return;
      }
      const issue = validationIssues(err).find((i) => i.path === 'bodyMd');
      if (issue) setBodyError(issue.message);
      else setError(errorMessage(err));
    },
  });

  const startEdit = () => {
    setBody(profile.bodyMd);
    setNote('');
    setError(null);
    setBodyError(null);
    setConflict(false);
    setEditing(true);
  };

  const submit = (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    const input = { kind: profile.kind, bodyMd: body, changeNote: note.trim() || null, baseVersion: profile.versionNo };
    const parsed = saveContextProfileInput.safeParse(input);
    if (!parsed.success) {
      setBodyError(parsed.error.issues[0]?.message ?? 'Sprawdź treść');
      return;
    }
    if (body === profile.bodyMd) {
      setBodyError('Treść jest taka sama jak w bieżącej wersji.');
      return;
    }
    setBodyError(null);
    save.mutate(input);
  };

  return (
    <section id={profile.kind} aria-labelledby={headingId} className="scroll-mt-6 border-b border-line pb-6">
      <div className="flex flex-col gap-3 md:flex-row md:items-start">
        <div className="min-w-0 flex-1">
          <h3 id={headingId} className="text-body-s font-semibold text-fg">
            {label} <Mono className="text-caption text-fg-muted">{profile.file}</Mono>
          </h3>
          <p className="text-caption text-fg-muted">{profileMeta(profile)}</p>
        </div>
        {!editing && (
          <div className="flex shrink-0 items-center gap-2">
            {profile.versionNo > 0 && (
              <Button
                variant="ghost"
                size="s"
                className={TOUCH_BUTTON}
                icon={History}
                onClick={() => setHistoryOpen(true)}
              >
                Historia
              </Button>
            )}
            {profile.canEdit && (
              <Button variant="secondary" size="s" className={TOUCH_BUTTON} icon={Pencil} onClick={startEdit}>
                Edytuj
              </Button>
            )}
          </div>
        )}
      </div>

      {editing ? (
        <form onSubmit={submit} noValidate className="mt-4 flex flex-col gap-4" aria-label={`Edycja ${profile.file}`}>
          {conflict && (
            <FormMessage>
              <span className="flex flex-col gap-2">
                Profil zmienił się w międzyczasie. Wczytaj najnowszą wersję i wprowadź zmiany ponownie.
                <span>
                  <Button
                    variant="secondary"
                    size="s"
                    className={TOUCH_BUTTON}
                    onClick={() => {
                      setEditing(false);
                      void utils.context.overview.invalidate();
                    }}
                  >
                    Wczytaj najnowszą wersję
                  </Button>
                </span>
              </span>
            </FormMessage>
          )}
          {error && <FormMessage>{error}</FormMessage>}
          <NoteEditor
            label="Treść"
            value={body}
            onChange={setBody}
            rows={16}
            error={bodyError}
            hint={CONTEXT_PRIVATE_DATA_NOTE}
          />
          <Field label="Opis zmiany (opcjonalnie)">
            <Input
              value={note}
              onChange={(e) => setNote(e.target.value)}
              maxLength={500}
              placeholder="Np. nowe stawki od listopada"
            />
          </Field>
          <div className="flex flex-wrap justify-end gap-2">
            <Button variant="ghost" onClick={() => setEditing(false)} disabled={save.isPending}>
              Anuluj
            </Button>
            <Button variant="primary" type="submit" loading={save.isPending} disabled={conflict}>
              Zapisz nową wersję
            </Button>
          </div>
        </form>
      ) : profile.versionNo > 0 ? (
        <div className="mt-4 max-h-80 overflow-y-auto rounded-lg border border-line px-4 py-3">
          <Markdown className="text-body-s">{profile.bodyMd}</Markdown>
        </div>
      ) : (
        <p className="mt-3 text-body-s text-fg-muted">
          {profile.canEdit ? 'Profil jest pusty. Uzupełnij go, aby AI znało Twój kontekst.' : 'Profil jest pusty.'}
        </p>
      )}
      <ProfileHistoryDialog kind={profile.kind} open={historyOpen} onOpenChange={setHistoryOpen} />
    </section>
  );
}
